import React, { useRef, useEffect, forwardRef, useState } from "react";
import { Loading, Paragraph, useColorTokens } from "@tvg/design-system";
import { animated, useSpring, useTransition } from "@react-spring/web";
// Types
import { RaceTypeCodeEnum } from "@tvg/ts-types/Race";
import { RunnerInfoUIComponentProps } from "../types";
import InlineAnalyses from "../../InlineAnalyses";
import RunnerChanges from "../../RunnerChanges";
// Components
import AdvancedInfo from "../../AdvancedInfo";
import PerformanceInfo from "../../PerformanceInfo";
import InlinePastPerformance from "../../InlinePastPerformances";
import ProfileNPps from "../../ProfileNPPs";
import {
  BetButtonsContainer,
  Container,
  Content,
  ContentContainer,
  ContentWrapper,
  InlineAnalysesContainer,
  LoadingContainer,
  PastPerformancesContainer,
  RunnerChangesContainer,
  SilkImage,
  SilkContainer
} from "./styled-components";

const AnimatedContainer = animated(Container);
const AnimatedContent = animated(Content);

const InlineAnalysesEmpty = () => {
  const colorTokens = useColorTokens();
  return (
    <Paragraph
      qaLabel="Inline-analyses-empty-message"
      color={colorTokens.content.subtle}
    >
      No comment available for this race
    </Paragraph>
  );
};

const MobileRunnerInfo = forwardRef<HTMLDivElement, RunnerInfoUIComponentProps>(
  (
    {
      loading,
      isOpen,
      isWPSBetType,
      isDriver,
      runnerBI,
      bestPerformanceRunners,
      trackDataSource,
      pastRaces,
      handleSelectRace,
      currentRace,
      entityRunnerId,
      runnerDob,
      runnerName,
      selectedRacePP,
      setSelectedRacePP,
      emptyComments,
      emptyCommentsHandicapping,
      timeform,
      handicapping,
      changes,
      runnerNumber,
      onClickVideo,
      children
    },
    ref
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [hasSilkError, setSilkError] = useState(false);
    const hasSilk = !!runnerBI.silkImage && !hasSilkError;

    const transition = useTransition(isOpen, {
      config: { duration: 300 },
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 }
    });

    const [style, animate] = useSpring(
      () => ({ height: 0, config: { duration: 300 } }),
      []
    );

    useEffect(() => {
      if (isOpen && contentRef.current) {
        animate({
          height: loading ? 76 : contentRef.current.offsetHeight
        });
      } else {
        animate({
          height: 0
        });
      }
      // Keep children as a dep for when the user changes betType and betButtons show
      // SilkImage and hasSilkError because when it displays text can take more space
      // This type of changes need to be considered to animate the content to its new height
    }, [
      animate,
      contentRef,
      loading,
      isOpen,
      children,
      runnerBI.silkImage,
      hasSilkError,
      changes
    ]);

    return (
      <>
        {transition(
          (styles, item) =>
            item && (
              <AnimatedContainer
                data-qa-label="runner-handicap-inline-container"
                style={styles}
                ref={ref}
              >
                <AnimatedContent style={style}>
                  <ContentContainer ref={contentRef}>
                    {loading ? (
                      <LoadingContainer>
                        <Loading
                          size="s"
                          qaLabel="runner-handicap-inline-loading"
                        />
                      </LoadingContainer>
                    ) : (
                      <ContentWrapper>
                        <AdvancedInfo
                          runnerInfo={runnerBI}
                          isScratched={runnerBI.isScratched}
                          isWPSBetType={isWPSBetType}
                          isInlineHandicapping
                          bestPerformanceRunners={bestPerformanceRunners}
                          hasMobileOrder={false}
                          hasSilk={false}
                        />
                        <PerformanceInfo
                          runnerInfo={runnerBI}
                          isScratched={runnerBI.isScratched}
                          isDriver={isDriver}
                          isInlineHandicapping
                          bestPerformanceRunners={bestPerformanceRunners}
                          hasMobileOrder={false}
                          hasSilk={false}
                        />
                        <BetButtonsContainer>{children}</BetButtonsContainer>
                        <InlineAnalysesContainer>
                          {!emptyComments && !emptyCommentsHandicapping ? (
                            <InlineAnalyses
                              trackDataSource={trackDataSource}
                              timeform={timeform}
                              handicapping={handicapping}
                            />
                          ) : (
                            <InlineAnalysesEmpty />
                          )}
                          <SilkContainer>
                            {hasSilk && (
                              <SilkImage
                                src={runnerBI.silkImage}
                                alt={`Silk for runner number ${runnerBI.number}`}
                                onError={() => setSilkError(true)}
                              />
                            )}
                          </SilkContainer>
                        </InlineAnalysesContainer>
                        {changes?.jockey &&
                        changes?.jockey.some(
                          (val) => val.runnerId === runnerNumber
                        ) ? (
                          <RunnerChangesContainer>
                            <RunnerChanges
                              changes={changes}
                              runnerNumber={runnerNumber}
                              raceTypeCode={
                                currentRace.type?.code || RaceTypeCodeEnum.ALL
                              }
                            />
                          </RunnerChangesContainer>
                        ) : null}
                        <PastPerformancesContainer
                          hasBorderTop={pastRaces.length === 0}
                        >
                          <InlinePastPerformance
                            races={pastRaces}
                            onSelectRace={handleSelectRace}
                            currentRace={currentRace}
                            currentRaceDistance={currentRace.distance}
                            currentRaceSurface={currentRace.surface}
                            isMobile
                          />
                        </PastPerformancesContainer>
                        {currentRace && (
                          <ProfileNPps
                            entityRunnerId={entityRunnerId}
                            currentRace={currentRace}
                            runnerName={runnerName}
                            runnerDob={runnerDob}
                            selectedRacePP={selectedRacePP}
                            setSelectedRacePP={setSelectedRacePP}
                            onClickVideo={onClickVideo}
                            device="mobile"
                          />
                        )}
                      </ContentWrapper>
                    )}
                  </ContentContainer>
                </AnimatedContent>
              </AnimatedContainer>
            )
        )}
      </>
    );
  }
);

export default MobileRunnerInfo;
